"use client"

import { Table, Text } from "@modules/common/components/ui"
import { updateLineItem } from "@lib/data/cart"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import { HttpTypes } from "@medusajs/types"
import { Spinner } from "@medusajs/icons"
import CartItemSelect from "@modules/cart/components/cart-item-select"
import ErrorMessage from "@modules/checkout/components/error-message"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemOptions from "@modules/common/components/line-item-options"
import LineItemPrice from "@modules/common/components/line-item-price"
import LineItemUnitPrice from "@modules/common/components/line-item-unit-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import { useState } from "react"

type ItemProps = {
  item: HttpTypes.StoreCartLineItem
  type?: "full" | "preview"
  currencyCode: string
}

const Item = ({ item, type = "full", currencyCode }: ItemProps) => {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useFrontendPreferences()

  const changeQuantity = async (quantity: number) => {
    setError(null)
    setUpdating(true)

    await updateLineItem({
      lineId: item.id,
      quantity,
    })
      .catch((err) => {
        setError(err.message)
      })
      .finally(() => {
        setUpdating(false)
      })
  }

  // TODO: Update this to grab the actual max inventory
  const maxQtyFromInventory = 10
  const maxQuantity = item.variant?.manage_inventory ? 10 : maxQtyFromInventory

  if (type === "full") {
    return (
      <li
        className="sahil-bag__line flex gap-x-4 small:gap-x-6 p-4 small:p-5"
        data-testid="product-row"
      >
        <LocalizedClientLink
          href={`/products/${item.product_handle}`}
          className="sahil-bag__thumb w-24 small:w-32 flex-none"
        >
          <Thumbnail
            thumbnail={item.thumbnail}
            images={item.variant?.product?.images}
            size="square"
          />
        </LocalizedClientLink>

        <div className="flex flex-1 min-w-0 flex-col justify-between gap-y-3">
          <div className="flex items-start justify-between gap-x-4">
            <div className="sahil-bag__item-details min-w-0">
              <LocalizedClientLink href={`/products/${item.product_handle}`}>
                <Text
                  className="font-semibold text-base-regular"
                  data-testid="product-title"
                >
                  {item.product_title}
                </Text>
              </LocalizedClientLink>
              <LineItemOptions
                variant={item.variant}
                label={t.cart.variant}
                data-testid="product-variant"
              />
              <div className="mt-1 text-small-regular sahil-acc-muted hidden xsmall:block">
                <LineItemUnitPrice
                  item={item}
                  style="tight"
                  currencyCode={currencyCode}
                />
              </div>
            </div>
            <div className="font-semibold whitespace-nowrap" dir="ltr">
              <LineItemPrice
                item={item}
                style="tight"
                currencyCode={currencyCode}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-x-3">
            <div className="flex items-center gap-x-2">
              <CartItemSelect
                value={item.quantity}
                onChange={(value) =>
                  changeQuantity(parseInt(value.target.value))
                }
                className="sahil-bag__qty w-20 h-10 p-0"
                data-testid="product-select-button"
              >
                {Array.from(
                  { length: Math.min(maxQuantity, 10) },
                  (_, i) => (
                    <option value={i + 1} key={i}>
                      {i + 1}
                    </option>
                  )
                )}
              </CartItemSelect>
              {updating && <Spinner className="animate-spin" />}
            </div>
            <DeleteButton
              id={item.id}
              className="sahil-bag__remove"
              data-testid="product-delete-button"
            >
              {t.cart.remove}
            </DeleteButton>
          </div>
          <ErrorMessage error={error} data-testid="product-error-message" />
        </div>
      </li>
    )
  }

  return (
    <Table.Row className="w-full" data-testid="product-row">
      <Table.Cell className="!pl-0 p-4 w-24">
        <LocalizedClientLink
          href={`/products/${item.product_handle}`}
          className="flex w-16"
        >
          <Thumbnail
            thumbnail={item.thumbnail}
            images={item.variant?.product?.images}
            size="square"
          />
        </LocalizedClientLink>
      </Table.Cell>

      <Table.Cell className="sahil-bag__item-details text-left">
        <Text
          className="txt-medium-plus text-ui-fg-base"
          data-testid="product-title"
        >
          {item.product_title}
        </Text>
        <LineItemOptions
          variant={item.variant}
          label={t.cart.variant}
          data-testid="product-variant"
        />
      </Table.Cell>

      <Table.Cell className="!pr-0">
        <span className="!pr-0 flex flex-col items-end h-full justify-center">
          <span className="flex gap-x-1 ">
            <Text className="text-ui-fg-muted">{item.quantity}x </Text>
            <LineItemUnitPrice
              item={item}
              style="tight"
              currencyCode={currencyCode}
            />
          </span>
          <LineItemPrice
            item={item}
            style="tight"
            currencyCode={currencyCode}
          />
        </span>
      </Table.Cell>
    </Table.Row>
  )
}

export default Item
